import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ChevronLeft, ChevronRight, RefreshCw, Search } from "lucide-react";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const errorMessage = (error) => {
  if (error?.code === "ECONNABORTED") return "Book search timed out. Please retry.";
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail : "Book search is unavailable right now. Please retry.";
};

export default function Books() {
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retryToken, setRetryToken] = useState(0);
  const requestId = useRef(0);

  useEffect(() => {
    if (!query) return;
    const controller = new AbortController();
    const id = ++requestId.current;
    setLoading(true); setError("");
    api.get("/books/search", {
      params: { q: query, page }, signal: controller.signal, timeout: 9000,
    }).then(({ data: result }) => {
      if (id === requestId.current) setData(result);
    }).catch((requestError) => {
      if (id === requestId.current && requestError?.code !== "ERR_CANCELED") {
        setError(errorMessage(requestError));
      }
    }).finally(() => {
      if (id === requestId.current) setLoading(false);
    });
    return () => controller.abort();
  }, [query, page, retryToken]);

  const submit = (event) => {
    event.preventDefault();
    const cleaned = input.trim();
    if (!cleaned) return;
    setPage(1); setQuery(cleaned); setData(null); setError("");
  };

  return (
    <div className="space-y-7" data-testid="books-page">
      <header>
        <div className="chip mb-2">Project Gutenberg</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Books</h1>
        <p className="text-slate-600 mt-1">Search free public-domain books by title or author.</p>
      </header>

      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 max-w-3xl">
        <label className="sr-only" htmlFor="book-search">Book title or author</label>
        <input id="book-search" data-testid="book-search-input" value={input} maxLength={120} onChange={(event)=>setInput(event.target.value)}
          placeholder="Try The Secret Garden or Frederick Douglass"
          className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
        <button data-testid="book-search-submit" disabled={loading || !input.trim()} className="inline-flex justify-center items-center gap-2 px-6 py-3 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold disabled:opacity-50">
          <Search size={16} /> Search
        </button>
      </form>

      {!query && (
        <div data-testid="books-initial-state" className="p-8 rounded-2xl bg-sky-50 border border-sky-100 text-center">
          <BookOpen size={30} className="mx-auto text-sky-700" />
          <div className="mt-3 font-display text-lg font-semibold text-slate-900">Find your next read</div>
          <p className="mt-1 text-sm text-slate-600">Enter a title or author above. Search begins only when you ask.</p>
        </div>
      )}

      {loading && (
        <div data-testid="books-loading" aria-busy="true" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} className="h-44 rounded-2xl" />)}
        </div>
      )}

      {!loading && error && (
        <div data-testid="books-error" role="alert" className="p-5 rounded-2xl bg-red-50 border border-red-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div><div className="font-semibold text-red-900">Search could not finish</div><div className="text-sm text-red-800 mt-1">{error}</div></div>
          <button type="button" data-testid="books-retry" onClick={()=>setRetryToken((value)=>value+1)} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-semibold"><RefreshCw size={14} /> Retry</button>
        </div>
      )}

      {!loading && !error && data && data.results?.length === 0 && (
        <div data-testid="books-empty" className="p-8 rounded-2xl bg-slate-50 border border-slate-200 text-center text-slate-600">No books found for “{query}”. Try a different title or author.</div>
      )}

      {!loading && !error && data?.results?.length > 0 && (
        <>
          <div className="flex items-baseline gap-2"><h2 className="font-display text-xl font-semibold">Results</h2><span className="text-sm text-slate-500">{data.count?.toLocaleString()} matches</span></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="books-results">
            {data.results.map((book) => {
              const cover = book.formats?.["image/jpeg"];
              const author = book.authors?.map((item)=>item.name).join(", ") || "Unknown author";
              return (
                <Link key={book.id} to={`/books/${book.id}`} data-testid={`book-result-${book.id}`} className="group p-4 rounded-2xl bg-white border border-slate-200 hover:border-teal-300 grid grid-cols-[72px_1fr] gap-4 min-w-0">
                  <div className="aspect-[2/3] rounded-lg bg-slate-100 overflow-hidden grid place-items-center">
                    {cover ? <img src={cover} alt="" className="w-full h-full object-cover" /> : <BookOpen size={22} className="text-slate-400" />}
                  </div>
                  <div className="min-w-0"><div className="font-display font-semibold text-slate-900 line-clamp-3 group-hover:text-teal-800">{book.title}</div><div className="text-sm text-slate-500 mt-1 line-clamp-2">{author}</div><div className="text-xs text-slate-400 mt-3">{book.download_count?.toLocaleString()} downloads</div></div>
                </Link>
              );
            })}
          </div>
          <div className="flex items-center justify-center gap-3">
            <button data-testid="books-previous" disabled={!data.previous || loading} onClick={()=>setPage((value)=>Math.max(1,value-1))} className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-slate-200 disabled:opacity-40"><ChevronLeft size={15} /> Previous</button>
            <span data-testid="books-page-number" className="text-sm text-slate-500">Page {page}</span>
            <button data-testid="books-next" disabled={!data.next || loading} onClick={()=>setPage((value)=>value+1)} className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-slate-200 disabled:opacity-40">Next <ChevronRight size={15} /></button>
          </div>
        </>
      )}
    </div>
  );
}